// ==============================================
// Booking Calendar — drag-to-select grid (30-min slots)
// ==============================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Club, ClubTable, BookingFormData } from '../types';
import { bookingsAPI } from '../services/api';
import Modal from './Modal';
import PlayerSearchPicker from './PlayerSearchPicker';
import { User } from '../types';
import { useAuth } from '../contexts/AuthContext';

// ── constants ────────────────────────────────────────────────

const SLOT_H = 28; // px per 30-min row

// ── helpers ──────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0');

function localIso(d: Date = new Date()): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const TODAY = localIso();

function parseOpenHours(s?: string | null): { start: number; end: number } {
  if (!s) return { start: 8, end: 23 };
  const m = s.match(/(\d{1,2}):\d{2}\s*[–\-]\s*(\d{1,2}):\d{2}/);
  return m ? { start: parseInt(m[1], 10), end: parseInt(m[2], 10) } : { start: 8, end: 23 };
}

// slotIdx → "HH:MM" (each slot = 30 min, index 0 = dayStart:00)
function slotToStr(slotIdx: number, dayStart: number): string {
  const totalMins = dayStart * 60 + slotIdx * 30;
  return `${pad(Math.floor(totalMins / 60))}:${totalMins % 60 === 0 ? '00' : '30'}`;
}

function fmtDate(iso: string): string {
  const [y, mo, day] = iso.split('-').map(Number);
  return new Date(y, mo - 1, day).toLocaleDateString('ro-RO', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

function shiftDate(iso: string, days: number): string {
  const [y, mo, day] = iso.split('-').map(Number);
  return localIso(new Date(y, mo - 1, day + days));
}

function durationStr(slots: number): string {
  const h = slots * 0.5;
  return h % 1 === 0 ? `${h}h` : `${h}h`;
}

// ── types ────────────────────────────────────────────────────

interface OccupiedSlot {
  table_id: number;
  start_time: string;
  end_time: string;
}

interface Selection {
  tableId: number;
  startSlot: number;
  endSlot: number; // inclusive
}

interface DragState {
  tableId: number;
  startSlot: number;
  endSlot: number;
}

interface Props {
  club: Club;
  tables: ClubTable[];
  onSuccess: () => void;
}

// ── component ────────────────────────────────────────────────

const BookingCalendar: React.FC<Props> = ({ club, tables, onSuccess }) => {
  const { user: currentUser } = useAuth();
  const { start: dayStart, end: dayEnd } = parseOpenHours(club.open_hours);
  const slotCount = (dayEnd - dayStart) * 2;
  const slots = Array.from({ length: slotCount }, (_, i) => i);

  const [date, setDate] = useState(TODAY);
  const [occupied, setOccupied] = useState<OccupiedSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const dragRef     = useRef<DragState | null>(null);
  const prevSelRef  = useRef<Selection | null>(null); // selection captured at mousedown
  const [dragSnapshot, setDragSnapshot] = useState<DragState | null>(null);

  const [selection, setSelection] = useState<Selection | null>(null);
  const [hoverCell, setHoverCell] = useState<{ tableId: number; slot: number } | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [participants, setParticipants] = useState<User[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  // ── fetch occupied slots ─────────────────────────────────

  const fetchSlots = useCallback(async (d: string) => {
    setLoadingSlots(true);
    try {
      const res = await bookingsAPI.getClubSlots(club.id, d);
      if (res.success) setOccupied(res.data?.slots ?? []);
    } catch { /* silent */ }
    finally { setLoadingSlots(false); }
  }, [club.id]);

  useEffect(() => {
    fetchSlots(date);
    setSelection(null);
  }, [date, fetchSlots]);

  // ── slot state helpers ───────────────────────────────────

  const isBooked = useCallback((tableId: number, slotIdx: number): boolean => {
    const slotStart = dayStart * 60 + slotIdx * 30;
    const slotEnd   = slotStart + 30;
    return occupied.some(s => {
      if (s.table_id !== tableId) return false;
      const [bSh, bSm] = s.start_time.split(':').map(Number);
      const [bEh, bEm] = s.end_time.split(':').map(Number);
      const bStart = bSh * 60 + bSm;
      const bEnd   = bEh * 60 + bEm;
      return slotStart < bEnd && slotEnd > bStart;
    });
  }, [occupied, dayStart]);

  const inDrag = (tableId: number, slotIdx: number): boolean => {
    if (!dragSnapshot || dragSnapshot.tableId !== tableId) return false;
    const lo = Math.min(dragSnapshot.startSlot, dragSnapshot.endSlot);
    const hi = Math.max(dragSnapshot.startSlot, dragSnapshot.endSlot);
    return slotIdx >= lo && slotIdx <= hi;
  };

  const inSelection = (tableId: number, slotIdx: number): boolean => {
    if (!selection || selection.tableId !== tableId) return false;
    return slotIdx >= selection.startSlot && slotIdx <= selection.endSlot;
  };

  const cellState = (tableId: number, slotIdx: number) => {
    if (isBooked(tableId, slotIdx)) return 'booked' as const;
    if (inDrag(tableId, slotIdx))   return 'dragging' as const;
    if (inSelection(tableId, slotIdx)) return 'selected' as const;
    return 'free' as const;
  };

  // ── drag / click handlers ────────────────────────────────

  const clampEnd = useCallback((tableId: number, startSlot: number, rawEnd: number): number => {
    if (rawEnd >= startSlot) {
      let end = startSlot;
      for (let s = startSlot + 1; s <= rawEnd; s++) {
        if (isBooked(tableId, s)) break;
        end = s;
      }
      return end;
    } else {
      let end = startSlot;
      for (let s = startSlot - 1; s >= rawEnd; s--) {
        if (isBooked(tableId, s)) break;
        end = s;
      }
      return end;
    }
  }, [isBooked]);

  const onCellDown = (tableId: number, slotIdx: number) => {
    if (isBooked(tableId, slotIdx)) return;
    prevSelRef.current = selection;           // capture before clearing
    dragRef.current = { tableId, startSlot: slotIdx, endSlot: slotIdx };
    setDragSnapshot({ tableId, startSlot: slotIdx, endSlot: slotIdx });
    setSelection(null);
  };

  const onCellEnter = (tableId: number, slotIdx: number) => {
    if (!dragRef.current || dragRef.current.tableId !== tableId) return;
    const clamped = clampEnd(tableId, dragRef.current.startSlot, slotIdx);
    dragRef.current.endSlot = clamped;
    setDragSnapshot({ ...dragRef.current });
  };

  // Global mouseup — finalise drag or click into selection
  useEffect(() => {
    const up = () => {
      const d = dragRef.current;
      if (!d) return;

      const startS   = Math.min(d.startSlot, d.endSlot);
      const endS     = Math.max(d.startSlot, d.endSlot);
      const wasClick = d.startSlot === d.endSlot; // no movement between down and up
      const prev     = prevSelRef.current;

      if (wasClick && prev && prev.tableId === d.tableId) {
        const s = d.startSlot;
        if (s === prev.startSlot - 1) {
          // Adjacent above — extend backward
          setSelection({ ...prev, startSlot: s });
        } else if (s === prev.endSlot + 1) {
          // Adjacent below — extend forward
          setSelection({ ...prev, endSlot: s });
        } else {
          // Not adjacent or different table — new single-slot selection
          setSelection({ tableId: d.tableId, startSlot: s, endSlot: s });
        }
      } else {
        // Drag (or no prior selection) — use the dragged range as-is
        setSelection({ tableId: d.tableId, startSlot: startS, endSlot: endS });
      }

      dragRef.current = null;
      setDragSnapshot(null);
    };
    document.addEventListener('mouseup', up);
    return () => document.removeEventListener('mouseup', up);
  }, []);

  // ── booking submit ───────────────────────────────────────

  const handleBook = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selection) return;
    setBookingError(null);

    const payload: BookingFormData = {
      club_id:         club.id,
      table_id:        selection.tableId,
      booking_date:    date,
      start_time:      slotToStr(selection.startSlot, dayStart),
      end_time:        slotToStr(selection.endSlot + 1, dayStart),
      notes:           notes || undefined,
      participant_ids: participants.length > 0 ? participants.map(p => p.id) : undefined,
    };

    try {
      setSubmitting(true);
      const res = await bookingsAPI.create(payload);
      if (res.success) {
        setShowModal(false);
        setSelection(null);
        setNotes('');
        setParticipants([]);
        fetchSlots(date);
        onSuccess();
      } else {
        setBookingError(res.message || 'Rezervarea a eșuat.');
      }
    } catch (err: any) {
      setBookingError(err.message || 'A apărut o eroare.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── derived strings ──────────────────────────────────────

  const selStartStr  = selection ? slotToStr(selection.startSlot, dayStart) : '';
  const selEndStr    = selection ? slotToStr(selection.endSlot + 1, dayStart) : '';
  const selDuration  = selection ? durationStr(selection.endSlot - selection.startSlot + 1) : '';
  const selTableName = selection ? (tables.find(t => t.id === selection.tableId)?.table_number ?? '') : '';

  // ── render ───────────────────────────────────────────────

  return (
    <div className="booking-calendar" style={{ userSelect: 'none', pointerEvents: showModal ? 'none' : undefined }}>

      {/* ── Date nav ── */}
      <div className="booking-date-nav" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1rem', flexWrap: 'wrap', gap: '.75rem',
      }}>
        <div className="booking-date-controls" style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
          <button
            onClick={() => setDate(d => { const p = shiftDate(d, -1); return p >= TODAY ? p : d; })}
            disabled={date <= TODAY}
            style={{
              width: 36, height: 36, borderRadius: 10, border: '1px solid var(--tt-border)',
              background: 'var(--tt-surface)', cursor: date <= TODAY ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: date <= TODAY ? .4 : 1, fontSize: '1rem',
            }}
          >‹</button>

          <div className="booking-date-picker" style={{ position: 'relative' }}>
            <div style={{
              padding: '.45rem 1rem', borderRadius: 10, border: '1px solid var(--tt-border)',
              background: 'var(--tt-surface)', fontWeight: 600, fontSize: '.9rem',
              cursor: 'pointer', minWidth: 220, textAlign: 'center',
            }}>
              {fmtDate(date)}
            </div>
            <input
              type="date" min={TODAY} value={date}
              onChange={e => { if (e.target.value >= TODAY) setDate(e.target.value); }}
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
            />
          </div>

          <button
            onClick={() => setDate(d => shiftDate(d, 1))}
            style={{
              width: 36, height: 36, borderRadius: 10, border: '1px solid var(--tt-border)',
              background: 'var(--tt-surface)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem',
            }}
          >›</button>
        </div>

        <div className="booking-legend" style={{ display: 'flex', alignItems: 'center', gap: '.75rem', fontSize: '.8rem', color: 'var(--tt-text-muted)' }}>
          {loadingSlots && <span className="spinner-border spinner-border-sm" style={{ color: 'var(--tt-primary)' }} />}
          {[
            { bg: '#fef2f2', border: '#fecaca', label: 'Rezervat' },
            { bg: '#bfdbfe', border: '#3b82f6', label: 'Selectat' },
          ].map(({ bg, border, label }) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '.3rem' }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: bg, border: `1px solid ${border}`, display: 'inline-block', flexShrink: 0 }} />
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* ── Grid ── */}
      <div className="booking-grid-shell" style={{
        border: '1px solid var(--tt-border)', borderRadius: 14,
        overflow: 'auto', maxHeight: 520, boxShadow: 'var(--sh-sm)',
      }}>
        <div className="booking-grid" style={{
          display: 'grid',
          gridTemplateColumns: `68px repeat(${tables.length}, minmax(96px, 1fr))`,
          minWidth: 68 + tables.length * 96,
          '--booking-table-count': tables.length,
        } as React.CSSProperties}>

          {/* Sticky header */}
          <div style={hdrCell(true)}>Ora</div>
          {tables.map(t => (
            <div key={t.id} style={hdrCell(false)}>
              <div style={{ fontWeight: 700, fontSize: '.82rem' }}>Masa {t.table_number}</div>
              {t.table_type && <div style={{ fontSize: '.72rem', color: 'var(--tt-text-muted)', marginTop: 1 }}>{t.table_type}</div>}
            </div>
          ))}

          {/* Slot rows */}
          {slots.map((slotIdx) => {
            const isHalfHour  = slotIdx % 2 === 1;
            const isLastSlot  = slotIdx === slots.length - 1;
            const borderColor = isHalfHour ? 'rgba(0,0,0,.06)' : 'var(--tt-border)';

            return (
              <React.Fragment key={slotIdx}>
                {/* Time label cell */}
                <div style={{
                  position: 'sticky', left: 0, zIndex: 1,
                  background: 'var(--tt-surface)',
                  borderRight: '1px solid var(--tt-border)',
                  borderBottom: isLastSlot ? 'none' : `1px solid ${borderColor}`,
                  height: SLOT_H,
                  display: 'flex', alignItems: 'center',
                  paddingLeft: 8,
                  fontSize: '.72rem', fontWeight: isHalfHour ? 400 : 600,
                  color: isHalfHour ? 'var(--tt-border)' : 'var(--tt-text-muted)',
                }}>
                  {isHalfHour ? '· · ·' : slotToStr(slotIdx, dayStart)}
                </div>

                {/* Table cells */}
                {tables.map((t, tIdx) => {
                  const state   = cellState(t.id, slotIdx);
                  const isHover = hoverCell?.tableId === t.id && hoverCell.slot === slotIdx;
                  const isLastTable = tIdx === tables.length - 1;
                  return (
                    <div
                      key={t.id}
                      style={cellStyle(state, isHover, isLastSlot, isLastTable, isHalfHour)}
                      onMouseDown={e => { e.preventDefault(); onCellDown(t.id, slotIdx); }}
                      onMouseEnter={() => { setHoverCell({ tableId: t.id, slot: slotIdx }); onCellEnter(t.id, slotIdx); }}
                      onMouseLeave={() => setHoverCell(null)}
                      onDragStart={e => e.preventDefault()}
                    >
                      {state === 'booked' && !isHalfHour && (
                        <span style={{ fontSize: '.62rem', color: '#dc2626', fontWeight: 600, pointerEvents: 'none', opacity: .8 }}>
                          Rezervat
                        </span>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <p style={{ fontSize: '.78rem', color: 'var(--tt-text-muted)', marginTop: '.6rem', textAlign: 'center' }}>
        Click sau trage pe o coloană pentru a selecta intervalul dorit
      </p>

      {/* ── Floating CTA ── */}
      {selection && !showModal && (
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          zIndex: 400, display: 'flex', alignItems: 'center', gap: '1rem',
          background: 'var(--tt-surface)', border: '1px solid var(--tt-border)',
          borderRadius: 16, padding: '.875rem 1.25rem',
          boxShadow: '0 8px 32px rgba(0,0,0,.18)',
          maxWidth: 'calc(100vw - 2rem)',
          animation: 'fadeInUp .2s ease',
        }} className="booking-selection-bar">
          <div className="booking-selection-info">
            <div style={{ fontWeight: 700, fontSize: '.9rem' }}>
              🏓 Masa {selTableName}
              <span style={{ color: 'var(--tt-text-muted)', fontWeight: 400, marginLeft: '.4rem' }}>
                {selStartStr} – {selEndStr}
              </span>
            </div>
            <div style={{ fontSize: '.78rem', color: 'var(--tt-text-muted)', marginTop: 1 }}>
              {fmtDate(date)} · {selDuration}
            </div>
          </div>

          <div className="booking-selection-actions" style={{ display: 'flex', gap: '.5rem', flexShrink: 0 }}>
            <button
              onClick={() => setSelection(null)}
              style={{
                width: 32, height: 32, minWidth: 32, padding: 0, borderRadius: '50%',
                border: '1px solid var(--tt-border)', background: 'var(--tt-surface-2)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--tt-text-muted)', boxSizing: 'border-box',
              }}
            >✕</button>
            <button
              className="btn btn-primary btn-sm"
              style={{ borderRadius: 10, fontWeight: 700, paddingLeft: '1.1rem', paddingRight: '1.1rem' }}
              onClick={() => { setBookingError(null); setShowModal(true); }}
            >
              Rezervă acum →
            </button>
          </div>
        </div>
      )}

      {/* ── Confirmation modal ── */}
      <Modal
        isOpen={showModal && !!selection}
        onClose={() => { setShowModal(false); setBookingError(null); }}
        title="Confirmare rezervare"
        footer={
          <div style={{ display: 'flex', gap: '.75rem' }}>
            <button
              type="button" className="btn btn-outline-secondary"
              onClick={() => { setShowModal(false); setBookingError(null); }}
              disabled={submitting} style={{ flex: 1, borderRadius: 10 }}
            >
              Anulează
            </button>
            <button
              type="submit" form="booking-confirm-form" className="btn btn-primary"
              disabled={submitting} style={{ flex: 2, borderRadius: 10, fontWeight: 700 }}
            >
              {submitting
                ? <><span className="spinner-border spinner-border-sm me-2" />Se rezervă…</>
                : '✓ Confirmă rezervarea'
              }
            </button>
          </div>
        }
      >
        <div style={{
          background: 'var(--tt-surface-2)', border: '1px solid var(--tt-border)',
          borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.25rem',
        }}>
          {([
            ['🏟️ Club',     club.name],
            ['🏓 Masă',     `Masa ${selTableName}`],
            ['📅 Data',     fmtDate(date)],
            ['🕐 Interval', `${selStartStr} – ${selEndStr} (${selDuration})`],
          ] as [string, string][]).map(([label, value]) => (
            <div key={label} style={{
              display: 'flex', justifyContent: 'space-between', padding: '.35rem 0',
              borderBottom: '1px solid var(--tt-border)', gap: '1rem',
            }}>
              <span style={{ color: 'var(--tt-text-muted)', fontSize: '.85rem' }}>{label}</span>
              <span style={{ fontWeight: 600, fontSize: '.85rem', textAlign: 'right' }}>{value}</span>
            </div>
          ))}
        </div>

        {bookingError && (
          <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '.85rem' }}>
            {bookingError}
          </div>
        )}

        <form id="booking-confirm-form" onSubmit={handleBook}>
          <div className="mb-3">
            <label className="form-label" style={{ fontWeight: 600 }}>
              Note <span style={{ color: 'var(--tt-text-muted)', fontWeight: 400 }}>(opțional)</span>
            </label>
            <textarea
              className="form-control" rows={2} value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Solicitări speciale…"
              style={{ borderRadius: 10, resize: 'none' }}
            />
          </div>

          <div style={{
            borderTop: '1px solid var(--tt-border)',
            paddingTop: '1rem',
          }}>
            <label className="form-label" style={{ fontWeight: 600, marginBottom: '.5rem', display: 'block' }}>
              Invită jucători{' '}
              <span style={{ color: 'var(--tt-text-muted)', fontWeight: 400 }}>(opțional)</span>
            </label>
            <p style={{ fontSize: '.78rem', color: 'var(--tt-text-muted)', marginBottom: '.75rem', lineHeight: 1.5 }}>
              Jucătorii invitați vor primi o notificare cu detaliile sesiunii.
            </p>
            <PlayerSearchPicker
              selected={participants}
              onAdd={u => setParticipants(prev => [...prev, u])}
              onRemove={id => setParticipants(prev => prev.filter(p => p.id !== id))}
              excludeIds={currentUser?.id ? [currentUser.id] : []}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ── style helpers ────────────────────────────────────────────

function hdrCell(corner: boolean): React.CSSProperties {
  return {
    position: 'sticky', top: 0,
    left: corner ? 0 : undefined,
    zIndex: corner ? 3 : 2,
    background: 'var(--tt-surface)',
    borderBottom: '2px solid var(--tt-border)',
    borderRight: corner ? '1px solid var(--tt-border)' : 'none',
    padding: '10px 8px', textAlign: 'center',
    fontSize: '.8rem', fontWeight: 700,
    color: corner ? 'var(--tt-text-muted)' : 'var(--tt-text)',
  };
}

function cellStyle(
  state: 'free' | 'booked' | 'dragging' | 'selected',
  isHover: boolean,
  isLastSlot: boolean,
  isLastTable: boolean,
  isHalfHour: boolean,
): React.CSSProperties {
  const borderColor = isHalfHour ? 'rgba(0,0,0,.06)' : 'var(--tt-border)';
  const base: React.CSSProperties = {
    height: SLOT_H,
    borderBottom: isLastSlot ? 'none' : `1px solid ${borderColor}`,
    borderRight: isLastTable ? 'none' : '1px solid var(--tt-border)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background .06s',
  };
  if (state === 'booked')    return { ...base, background: '#fef2f2', cursor: 'not-allowed' };
  if (state === 'dragging')  return { ...base, background: '#dbeafe', cursor: 'ns-resize' };
  if (state === 'selected')  return { ...base, background: '#bfdbfe', cursor: 'pointer' };
  return { ...base, background: isHover ? '#eff6ff' : 'var(--tt-surface)', cursor: 'cell' };
}

export default BookingCalendar;
